import { NotificationsObservable } from '../../../notifications/core/notifications-observable/implementation';
import { AllowAdvancedAbortSignalConstruct, ConstructAdvancedAbortSignal } from './constructor';
import { ADVANCED_ABORT_SIGNAL_PRIVATE } from './privates';
import { AdvancedAbortSignalNormalizeWrapPromiseOptions, ApplyOnAbortCallback, LinkAdvancedAbortSignalWithFetchArgumentsSpread, RaceAborted } from './functions';
import { IsPromiseLikeBase, PromiseTry } from '../../../promises/helpers';
import { clearImmediate, setImmediate } from '../../../classes/set-immediate';
export function NewAdvancedAbortSignal() {
    AllowAdvancedAbortSignalConstruct(true);
    const signal = new AdvancedAbortSignal();
    AllowAdvancedAbortSignalConstruct(false);
    return signal;
}
export function AdvancedAbortSignalGetAborted(instance) {
    return instance[ADVANCED_ABORT_SIGNAL_PRIVATE].aborted;
}
export function AdvancedAbortSignalGetReason(instance) {
    return instance[ADVANCED_ABORT_SIGNAL_PRIVATE].reason;
}
export function AdvancedAbortSignalWrapPromise(instance, promise, options) {
    return RaceAborted(instance, promise)
        .then((value) => {
        return instance.aborted
            ? ApplyOnAbortCallback(instance, options)
            : value;
    }, (error) => {
        if (instance.aborted) {
            return ApplyOnAbortCallback(instance, options);
        }
        else {
            throw error;
        }
    });
}
export function AdvancedAbortSignalWrapPromiseOrCreate(instance, promiseOrCallback, options) {
    if (typeof promiseOrCallback === 'function') {
        return AdvancedAbortSignalWrapFunction(instance, () => {
            return new Promise((resolve, reject) => {
                promiseOrCallback.call(instance, resolve, reject, instance);
            });
        }, options)();
    }
    else if (IsPromiseLikeBase(promiseOrCallback)) {
        return AdvancedAbortSignalWrapPromise(instance, promiseOrCallback, options);
    }
    else {
        throw new TypeError(`Expected Promise or function as signal.wrapPromise's first argument.`);
    }
}
export function AdvancedAbortSignalWrapFunction(instance, callback, options) {
    return function (...args) {
        return instance.aborted
            ? ApplyOnAbortCallback(instance, options)
            : AdvancedAbortSignalWrapPromise(instance, PromiseTry(() => callback.apply(instance, args)), options);
    };
}
export function AdvancedAbortSignalWrapFetchArguments(instance, requestInfo, requestInit) {
    return LinkAdvancedAbortSignalWithFetchArgumentsSpread(instance, requestInfo, requestInit);
}
export function AdvancedAbortSignalWhenAborted(instance, callback) {
    if (instance.aborted) {
        const timer = setImmediate(() => callback.call(instance, instance.reason));
        return () => {
            clearImmediate(timer);
        };
    }
    else {
        const clear = () => {
            listener.deactivate();
        };
        const listener = instance.addListener('abort', () => {
            clear();
            callback.call(instance, instance.reason);
        });
        listener.activate();
        return clear;
    }
}
export function AdvancedAbortSignalToAbortController(instance) {
    const controller = new AbortController();
    if (instance.aborted) {
        if (!controller.signal.aborted) {
            controller.abort();
        }
    }
    else {
        const clear = () => {
            controller.signal.removeEventListener('abort', clear, false);
            signalListener.deactivate();
        };
        const signalListener = instance.addListener('abort', () => {
            clear();
            controller.abort();
        });
        signalListener.activate();
        controller.signal.addEventListener('abort', clear, false);
    }
    return controller;
}
export class AdvancedAbortSignal extends NotificationsObservable {
    constructor() {
        let context;
        super((_context) => {
            context = _context;
        });
        ConstructAdvancedAbortSignal(this, context);
    }
    get aborted() {
        return AdvancedAbortSignalGetAborted(this);
    }
    get reason() {
        return AdvancedAbortSignalGetReason(this);
    }
    wrapPromise(promiseOrCallback, options) {
        return AdvancedAbortSignalWrapPromiseOrCreate(this, promiseOrCallback, AdvancedAbortSignalNormalizeWrapPromiseOptions(options));
    }
    wrapFunction(callback, options) {
        return AdvancedAbortSignalWrapFunction(this, callback, AdvancedAbortSignalNormalizeWrapPromiseOptions(options));
    }
    wrapFetchArguments(requestInfo, requestInit) {
        return AdvancedAbortSignalWrapFetchArguments(this, requestInfo, requestInit);
    }
    whenAborted(callback) {
        return AdvancedAbortSignalWhenAborted(this, callback);
    }
    toAbortController() {
        return AdvancedAbortSignalToAbortController(this);
    }
}
//# sourceMappingURL=implementation.js.map